import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  BarChart3, 
  Users, 
  Music, 
  TrendingUp, 
  Clock, 
  RefreshCw,
  Calendar,
  Filter,
  MessageSquare,
  AlertCircle,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { adminService } from '@/client/services/admin';
import { DateRangePicker } from '@/components/ui/date-picker';
import { format } from 'date-fns';

export interface AnnotationStats {
  totalAnnotations: number;
  uniqueParticipants: number;
  uniqueSongs: number;
  completedSessions: number;
  activeSessions: number;
  averageSessionTime: number;
  averageRatings: {
    aestheticQuality: number;
    playlistLikelihood: number;
    musicalCreativity: number;
    productionQuality: number;
    emotionalEngagement: number;
  };
  authenticityDistribution: Record<string, number>;
  sourceDistribution: Record<string, number>;
  aiAnnotations: number;
  humanAnnotations: number;
  recentActivity: {
    annotationsToday: number;
    annotationsThisWeek: number;
    newParticipantsToday: number;
    newParticipantsThisWeek: number;
  };
  feedbackMetrics?: {
    totalFeedback: number;
    unprocessedFeedback: number;
    averageFeedbackLength: number;
    feedbackByContext: Record<string, number>;
    feedbackEngagementRate: number; // percentage of participants who provided feedback
    recentFeedback: {
      feedbackToday: number;
      feedbackThisWeek: number;
    };
  };
}

interface StatsFilters {
  dateRange: 'today' | 'week' | 'month' | 'custom' | 'all';
  source?: string;
  participantId?: string;
  startDate?: Date;
  endDate?: Date;
}

interface AnnotationStatsProps {
  onRefresh?: () => void;
  onStatsUpdate?: (stats: AnnotationStats) => void;
  onError?: (error: string) => void;
  className?: string;
}

// Mock data for demonstration - replace with actual API calls
const mockStats: AnnotationStats = {
  totalAnnotations: 1247,
  uniqueParticipants: 89,
  uniqueSongs: 156,
  completedSessions: 67,
  activeSessions: 12,
  averageSessionTime: 18.5,
  averageRatings: {
    aestheticQuality: 6.8,
    playlistLikelihood: 5.9,
    musicalCreativity: 7.2,
    productionQuality: 7.1,
    emotionalEngagement: 6.5
  },
  authenticityDistribution: {
    'real': 423,
    'ai-generated': 651,
    'uncertain': 173
  },
  sourceDistribution: {
    'suno': 312,
    'mureka': 298,
    'sonauto': 321,
    'udio': 316,
    'human': 300
  },
  aiAnnotations: 1247,
  humanAnnotations: 300,
  recentActivity: {
    annotationsToday: 47,
    annotationsThisWeek: 234,
    newParticipantsToday: 3,
    newParticipantsThisWeek: 18
  },
  feedbackMetrics: {
    totalFeedback: 156,
    unprocessedFeedback: 23,
    averageFeedbackLength: 127,
    feedbackByContext: {
      'demographics': 45,
      'annotation': 89,
      'completion': 22
    },
    feedbackEngagementRate: 67.4, // 60 out of 89 participants provided feedback
    recentFeedback: {
      feedbackToday: 8,
      feedbackThisWeek: 34
    }
  }
};

function StatCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend, 
  className 
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: { value: number; label: string };
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
        {trend && (
          <div className="flex items-center pt-1">
            <TrendingUp className="h-3 w-3 text-green-600 mr-1" />
            <span className="text-xs text-green-600">
              +{trend.value} {trend.label}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RatingChart({ ratings }: { ratings: AnnotationStats['averageRatings'] }) {
  const ratingEntries = Object.entries(ratings);
  const maxRating = Math.max(...Object.values(ratings));

  return (
    <div className="space-y-3">
      {ratingEntries.map(([key, value]) => {
        const percentage = (value / 10) * 100;
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        
        return (
          <div key={key} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="font-medium">{label}</span>
              <span className="text-muted-foreground">{value.toFixed(1)}/10</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DistributionChart({ 
  data, 
  title, 
  colors 
}: { 
  data: Record<string, number>; 
  title: string;
  colors: string[];
}) {
  const total = Object.values(data).reduce((sum, value) => sum + value, 0);
  const entries = Object.entries(data);

  return (
    <div className="space-y-4">
      <h4 className="font-medium">{title}</h4>
      <div className="space-y-2">
        {entries.map(([key, value], index) => {
          const percentage = total > 0 ? (value / total) * 100 : 0;
          const color = colors[index % colors.length];
          
          return (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm capitalize">{key}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{value}</div>
                <div className="text-xs text-muted-foreground">
                  {percentage.toFixed(1)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AnnotationStatsComponent({ onRefresh, onStatsUpdate, onError, className }: AnnotationStatsProps) {
  const [stats, setStats] = useState<AnnotationStats>(mockStats);
  const [filters, setFilters] = useState<StatsFilters>({ 
    dateRange: 'all',
    source: undefined,
    participantId: undefined,
    startDate: undefined,
    endDate: undefined
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const loadStats = async () => {
    setIsLoading(true);
    try {
      // Convert filters to API format
      const apiFilters: any = {};
      
      if (filters.source) {
        apiFilters.songSource = filters.source;
      }
      
      if (filters.participantId) {
        apiFilters.participantId = filters.participantId;
      }
      
      // Handle date filtering
      if (filters.dateRange !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (filters.dateRange) {
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            apiFilters.startDate = format(startDate, 'yyyy-MM-dd');
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            apiFilters.startDate = format(startDate, 'yyyy-MM-dd');
            break;
          case 'month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            apiFilters.startDate = format(startDate, 'yyyy-MM-dd');
            break;
          case 'custom':
            if (filters.startDate) {
              apiFilters.startDate = format(filters.startDate, 'yyyy-MM-dd');
            }
            if (filters.endDate) {
              apiFilters.endDate = format(filters.endDate, 'yyyy-MM-dd');
            }
            break;
        }
      }
      
      // Use real API call with converted filters
      const response = await adminService.getFilteredStats(apiFilters);
      setStats(response.stats);
      setLastUpdated(new Date());
      
      // Notify parent component of stats update
      onStatsUpdate?.(response.stats);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load stats';
      console.error('Failed to load stats:', error);
      
      // Log additional debugging info
      const token = localStorage.getItem('auth-token');
      console.log('Auth token exists:', !!token);
      console.log('Token preview:', token ? `${token.substring(0, 10)}...` : 'none');
      
      // Notify parent of error
      onError?.(errorMessage);
      
      // Fallback to mock data with client-side filtering
      const filteredMockStats = applyClientSideFilters(mockStats, filters);
      setStats(filteredMockStats);
      setLastUpdated(new Date());
      
      // Notify parent component of stats update (even with mock data)
      onStatsUpdate?.(filteredMockStats);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [filters]);

  const handleRefresh = () => {
    loadStats();
    onRefresh?.();
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: 'all',
      source: undefined,
      participantId: undefined,
      startDate: undefined,
      endDate: undefined
    });
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const applyClientSideFilters = (baseStats: AnnotationStats, filters: StatsFilters): AnnotationStats => {
    // Create a filtered version of the stats based on filters
    let filteredStats = { ...baseStats };
    
    // Apply source filtering
    if (filters.source && filters.source !== 'all') {
      const sourceKey = filters.source;
      const sourceCount = baseStats.sourceDistribution[sourceKey] || 0;
      const totalOriginal = Object.values(baseStats.sourceDistribution).reduce((sum, val) => sum + val, 0);
      const filterRatio = totalOriginal > 0 ? sourceCount / totalOriginal : 0;
      
      // Scale down all stats proportionally
      filteredStats = {
        ...baseStats,
        totalAnnotations: Math.round(baseStats.totalAnnotations * filterRatio),
        uniqueParticipants: Math.round(baseStats.uniqueParticipants * filterRatio),
        uniqueSongs: Math.round(baseStats.uniqueSongs * filterRatio),
        completedSessions: Math.round(baseStats.completedSessions * filterRatio),
        activeSessions: Math.round(baseStats.activeSessions * filterRatio),
        sourceDistribution: {
          [sourceKey]: sourceCount,
          // Zero out other sources
          ...Object.keys(baseStats.sourceDistribution).reduce((acc, key) => {
            if (key !== sourceKey) acc[key] = 0;
            return acc;
          }, {} as Record<string, number>)
        },
        recentActivity: {
          annotationsToday: Math.round(baseStats.recentActivity.annotationsToday * filterRatio),
          annotationsThisWeek: Math.round(baseStats.recentActivity.annotationsThisWeek * filterRatio),
          newParticipantsToday: Math.round(baseStats.recentActivity.newParticipantsToday * filterRatio),
          newParticipantsThisWeek: Math.round(baseStats.recentActivity.newParticipantsThisWeek * filterRatio)
        }
      };
    }
    
    // Apply date range filtering
    if (filters.dateRange !== 'all') {
      let dateFilterRatio = 1;
      
      switch (filters.dateRange) {
        case 'today':
          dateFilterRatio = 0.05; // Assume today is ~5% of total data
          break;
        case 'week':
          dateFilterRatio = 0.2; // Assume this week is ~20% of total data
          break;
        case 'month':
          dateFilterRatio = 0.4; // Assume this month is ~40% of total data
          break;
        case 'custom':
          // For custom ranges, use a moderate filter ratio
          dateFilterRatio = 0.3;
          break;
      }
      
      // Apply date filtering to relevant stats
      filteredStats = {
        ...filteredStats,
        totalAnnotations: Math.round(filteredStats.totalAnnotations * dateFilterRatio),
        completedSessions: Math.round(filteredStats.completedSessions * dateFilterRatio),
        recentActivity: {
          ...filteredStats.recentActivity,
          annotationsToday: filters.dateRange === 'today' ? filteredStats.recentActivity.annotationsToday : Math.round(filteredStats.recentActivity.annotationsToday * dateFilterRatio),
          annotationsThisWeek: filters.dateRange === 'week' ? filteredStats.recentActivity.annotationsThisWeek : Math.round(filteredStats.recentActivity.annotationsThisWeek * dateFilterRatio)
        }
      };
    }
    
    return filteredStats;
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Annotation Statistics</h2>
          <div className="flex items-center gap-2">
            <p className="text-muted-foreground">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
            {(filters.source || filters.participantId || filters.dateRange !== 'all') && (
              <Badge variant="outline" className="text-xs">
                Filtered Results
              </Badge>
            )}
          </div>
        </div>
        <Button onClick={handleRefresh} disabled={isLoading} className="gap-2">
          <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
              {(filters.source || filters.participantId || filters.dateRange !== 'all') && (
                <Badge variant="secondary" className="text-xs">
                  Active
                </Badge>
              )}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearFilters}
              className="text-xs"
              disabled={filters.dateRange === 'all' && !filters.source && !filters.participantId}
            >
              Clear Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="dateRange">Date Range</Label>
                <Select
                  value={filters.dateRange}
                  onValueChange={(value: StatsFilters['dateRange']) =>
                    setFilters(prev => ({ ...prev, dateRange: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="source">Source</Label>
                <Select
                  value={filters.source || 'all'}
                  onValueChange={(value) =>
                    setFilters(prev => ({ ...prev, source: value === 'all' ? undefined : value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    <SelectItem value="suno">Suno</SelectItem>
                    <SelectItem value="mureka">Mureka</SelectItem>
                    <SelectItem value="sonauto">Sonauto</SelectItem>
                    <SelectItem value="udio">Udio</SelectItem>
                    <SelectItem value="human">Human</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="participantId">Participant ID</Label>
                <Input
                  id="participantId"
                  placeholder="Filter by participant..."
                  value={filters.participantId || ''}
                  onChange={(e) =>
                    setFilters(prev => ({ ...prev, participantId: e.target.value || undefined }))
                  }
                />
              </div>
            </div>

            {/* Custom Date Range Picker */}
            {filters.dateRange === 'custom' && (
              <div className="col-span-full">
                <Label>Custom Date Range</Label>
                <div className="mt-2 max-w-md">
                  <DateRangePicker
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    onStartDateSelect={(date) =>
                      setFilters(prev => ({ ...prev, startDate: date }))
                    }
                    onEndDateSelect={(date) =>
                      setFilters(prev => ({ ...prev, endDate: date }))
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Annotations"
          value={isLoading ? 'Loading...' : stats.totalAnnotations.toLocaleString()}
          subtitle="Across all sessions"
          icon={BarChart3}
          trend={!isLoading ? { value: stats.recentActivity.annotationsThisWeek, label: "this week" } : undefined}
        />
        <StatCard
          title="Participants"
          value={isLoading ? 'Loading...' : stats.uniqueParticipants}
          subtitle="Unique contributors"
          icon={Users}
          trend={!isLoading ? { value: stats.recentActivity.newParticipantsThisWeek, label: "this week" } : undefined}
        />
        <StatCard
          title="Songs Annotated"
          value={isLoading ? 'Loading...' : stats.uniqueSongs}
          subtitle="From all sources"
          icon={Music}
        />
        <StatCard
          title="Avg Session Time"
          value={isLoading ? 'Loading...' : formatTime(stats.averageSessionTime)}
          subtitle="Per completed session"
          icon={Clock}
        />
      </div>

      {/* AI vs Human Annotation Counts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="AI-Generated Annotations"
          value={isLoading ? 'Loading...' : stats.aiAnnotations.toLocaleString()}
          subtitle="Songs from Suno, Mureka, Sonauto, Udio"
          icon={Music}
        />
        <StatCard
          title="Human Song Annotations"
          value={isLoading ? 'Loading...' : stats.humanAnnotations.toLocaleString()}
          subtitle="Real human-composed songs"
          icon={Users}
        />
      </div>

      {/* Session Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Completed Sessions"
          value={stats.completedSessions}
          subtitle="Successfully finished"
          icon={TrendingUp}
          className="md:col-span-1"
        />
        <StatCard
          title="Active Sessions"
          value={stats.activeSessions}
          subtitle="Currently in progress"
          icon={Clock}
          className="md:col-span-1"
        />
      </div>

      {/* Feedback Metrics */}
      {stats.feedbackMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Feedback"
            value={stats.feedbackMetrics.totalFeedback}
            subtitle="Participant submissions"
            icon={MessageSquare}
            trend={{ value: stats.feedbackMetrics.recentFeedback.feedbackThisWeek, label: "this week" }}
          />
          <StatCard
            title="Unprocessed Feedback"
            value={stats.feedbackMetrics.unprocessedFeedback}
            subtitle="Awaiting review"
            icon={AlertCircle}
          />
          <StatCard
            title="Engagement Rate"
            value={`${stats.feedbackMetrics.feedbackEngagementRate.toFixed(1)}%`}
            subtitle="Participants providing feedback"
            icon={TrendingUp}
          />
          <StatCard
            title="Avg Feedback Length"
            value={`${stats.feedbackMetrics.averageFeedbackLength} chars`}
            subtitle="Character count"
            icon={FileText}
          />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Average Ratings */}
        <Card>
          <CardHeader>
            <CardTitle>Average Ratings</CardTitle>
            <p className="text-sm text-muted-foreground">
              Mean scores across all annotations
            </p>
          </CardHeader>
          <CardContent>
            <RatingChart ratings={stats.averageRatings} />
          </CardContent>
        </Card>

        {/* Authenticity Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Authenticity Assessment</CardTitle>
            <p className="text-sm text-muted-foreground">
              How participants classify songs
            </p>
          </CardHeader>
          <CardContent>
            <DistributionChart
              data={stats.authenticityDistribution}
              title="Distribution"
              colors={['#10b981', '#f59e0b', '#6b7280']}
            />
          </CardContent>
        </Card>

        {/* Source Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Source Distribution</CardTitle>
            <p className="text-sm text-muted-foreground">
              Annotations by music source
            </p>
          </CardHeader>
          <CardContent>
            <DistributionChart
              data={stats.sourceDistribution}
              title="Annotations by Source"
              colors={['#3b82f6', '#8b5cf6', '#06b6d4', '#f97316']}
            />
          </CardContent>
        </Card>

        {/* Feedback Context Distribution */}
        {stats.feedbackMetrics && (
          <Card>
            <CardHeader>
              <CardTitle>Feedback by Context</CardTitle>
              <p className="text-sm text-muted-foreground">
                Where participants provide feedback
              </p>
            </CardHeader>
            <CardContent>
              <DistributionChart
                data={stats.feedbackMetrics.feedbackByContext}
                title="Feedback Distribution"
                colors={['#10b981', '#3b82f6', '#f59e0b']}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {stats.recentActivity.annotationsToday}
              </div>
              <div className="text-sm text-muted-foreground">Annotations Today</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {stats.recentActivity.annotationsThisWeek}
              </div>
              <div className="text-sm text-muted-foreground">Annotations This Week</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.recentActivity.newParticipantsToday}
              </div>
              <div className="text-sm text-muted-foreground">New Participants Today</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.recentActivity.newParticipantsThisWeek}
              </div>
              <div className="text-sm text-muted-foreground">New Participants This Week</div>
            </div>
            {stats.feedbackMetrics && (
              <>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.feedbackMetrics.recentFeedback.feedbackToday}
                  </div>
                  <div className="text-sm text-muted-foreground">Feedback Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.feedbackMetrics.recentFeedback.feedbackThisWeek}
                  </div>
                  <div className="text-sm text-muted-foreground">Feedback This Week</div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}